import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";

/**
 * Runs an async loader and tracks its result.
 * `deps` re-run the loader; `reload()` re-runs it on demand after a mutation.
 */
export function useData(loader, deps = []) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const loaderRef = useRef(loader);
  loaderRef.current = loader;

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      setData(await loaderRef.current());
    } catch (e) {
      toast.error(e.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { reload(); }, deps);

  return { data, loading, reload, setData };
}

/** Wraps a mutation: shows a toast on success or failure, and tracks pending state. */
export function useAction() {
  const [pending, setPending] = useState(false);
  const run = useCallback(async (fn, successMessage) => {
    setPending(true);
    try {
      const result = await fn();
      if (successMessage) toast.success(successMessage);
      return { ok: true, result };
    } catch (e) {
      toast.error(e.message || "Something went wrong");
      return { ok: false };
    } finally {
      setPending(false);
    }
  }, []);
  return { run, pending };
}
